import { Play, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { F } from "@/components/ajustes/Field";

export const WpResyncPanel = ({ busy, resync, resyncLimit, setResyncLimit, resyncWp }) => (
  <>
    <div className="kicker mb-2">Subtítulos y extractos en WordPress</div>
    <p className="text-sm text-muted-foreground mb-4">
      Reescribe el subtítulo (campo del tema JNews) y el extracto de las noticias ya
      publicadas. Útil para las entradas anteriores al arreglo.
    </p>
    <F label="Cantidad de noticias (de la más reciente hacia atrás)">
      <Input type="number" min="1" max="200" value={resyncLimit}
        onChange={(e) => setResyncLimit(Number(e.target.value))}
        data-testid="resync-limit-input" />
    </F>
    <Button onClick={resyncWp} disabled={busy === "resync"} data-testid="resync-wp-btn">
      <Play className="h-4 w-4 mr-2" />
      {busy === "resync" ? "Reparando…" : "Reparar en WordPress"}
    </Button>
    {resync && (
      <div className="mt-4 text-sm" data-testid="resync-result">
        <p>Reparadas: <b>{resync.fixed || 0}</b> · Con error: <b>{resync.failed || 0}</b></p>
        <ul className="text-xs text-muted-foreground mt-2 space-y-1">
          {(resync.items || []).slice(-6).map((i) => (
            <li key={`${i.outlet}-${i.post_id || i.title}`}>{i.outlet}: {i.title}</li>
          ))}
          {(resync.errors || []).map((e) => (
            <li key={`e-${e.outlet}-${e.post_id}`} className="text-destructive">
              {e.outlet} · {e.post_id}: {e.error}
            </li>
          ))}
        </ul>
      </div>
    )}
  </>
);

export const CloudMediaPanel = ({ busy, media, migrateMedia }) => (
  <div className="border-t border-border pt-5 mt-6">
    <div className="kicker mb-2">Fotos en la nube</div>
    <p className="text-sm text-muted-foreground mb-3">
      Mueve al repositorio de objetos las imágenes que aún están guardadas dentro de la base
      de datos. La base queda liviana y los enlaces de las notas siguen funcionando igual.
    </p>
    <p className="text-sm mb-3" data-testid="media-pending">
      Pendientes de mover: <b>{media?.pending ?? "—"}</b>
      {media?.state === "running" && " · migración en curso…"}
      {typeof media?.moved === "number" && ` · movidas en el último trabajo: ${media.moved}`}
    </p>
    <Button onClick={migrateMedia} disabled={busy === "media" || media?.pending === 0}
            data-testid="migrate-media-btn">
      <Play className="h-4 w-4 mr-2" />
      {busy === "media" ? "Moviendo…" : "Mover imágenes a la nube"}
    </Button>
  </div>
);

export const AutoArchivePanel = ({ s, sec, busy, archive, archiveNow, save }) => (
  <div className="border-t border-border pt-5 mt-6">
    <div className="kicker mb-2">Archivado automático de la bandeja</div>
    <p className="text-sm text-muted-foreground mb-3">
      Archiva cada día las notas pendientes con más de X días. Ponga 0 para apagarlo.
    </p>
    <F label="Días de antigüedad" hint="0 = desactivado. Se revisa una vez al día.">
      <Input type="number" min="0" max="365"
             value={s.ingest?.auto_archive_days ?? 0}
             onChange={(e) => sec("ingest", "auto_archive_days", Number(e.target.value))}
             data-testid="auto-archive-days-input" />
    </F>
    <div className="flex flex-wrap items-center gap-3">
      <Button onClick={save} disabled={busy === "save"} data-testid="save-archive-btn">
        <Save className="h-4 w-4 mr-2" />Guardar
      </Button>
      <Button variant="outline" onClick={archiveNow} disabled={busy === "archive"}
              data-testid="archive-now-btn">
        <Play className="h-4 w-4 mr-2" />
        {busy === "archive" ? "Archivando…" : "Archivar ahora"}
      </Button>
      {archive && (
        <span className="text-sm text-muted-foreground" data-testid="archive-result">
          {archive.ran ? `${archive.archived} nota(s) archivadas (más de ${archive.days} días)`
                       : `Sin ejecutar: ${archive.reason}`}
        </span>
      )}
    </div>
  </div>
);

export const CapturesPanel = ({ busy, shots, runCaptures }) => (
  <div className="border-t border-border pt-5 mt-6">
    <div className="kicker mb-2">Capturas de los manuales</div>
    <p className="text-sm text-muted-foreground mb-3">
      Rehace las capturas reales de la aplicación que se incrustan en los manuales. Úselo
      cuando se cambie una pantalla. El navegador corre en el servidor y toma unos minutos.
    </p>
    <p className="text-sm mb-3" data-testid="captures-state">
      Capturas guardadas: <b>{shots?.files?.length ?? "—"}</b>
      {shots?.state === "running" &&
        ` · en curso: ${shots.done || 0} de ${shots.total || 0} (${shots.current || "…"})`}
      {shots?.state === "done" &&
        ` · último trabajo: ${shots.ok || 0} al día, ${shots.failed || 0} con error`}
      {shots?.state === "error" && ` · error: ${shots.error}`}
    </p>
    <div className="flex flex-wrap gap-2">
      <Button onClick={() => runCaptures("all")}
              disabled={busy.startsWith("shots") || shots?.state === "running"}
              data-testid="captures-all-btn">
        <Play className="h-4 w-4 mr-2" />
        {busy === "shots-all" ? "Capturando…" : "Actualizar todas"}
      </Button>
      <Button variant="outline" onClick={() => runCaptures("pantallas")}
              disabled={busy.startsWith("shots") || shots?.state === "running"}
              data-testid="captures-screens-btn">
        Solo las pantallas
      </Button>
      <Button variant="outline" onClick={() => runCaptures("editor")}
              disabled={busy.startsWith("shots") || shots?.state === "running"}
              data-testid="captures-editor-btn">
        Solo el editor
      </Button>
    </div>
    {(shots?.shots || []).length > 0 && (
      <ul className="text-xs mt-4 space-y-1 max-h-48 overflow-y-auto" data-testid="captures-log">
        {shots.shots.slice(-16).reverse().map((sh) => (
          <li key={sh.name} className={sh.ok ? "text-muted-foreground" : "text-destructive"}>
            {sh.ok ? "✓" : "✕"} {sh.name}
            {sh.ok ? ` · ${Math.round((sh.size || 0) / 1024)} KB` : ` · ${sh.detail}`}
          </li>
        ))}
      </ul>
    )}
  </div>
);
